/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { shallowRef } from "vue";

// CodeMirror core imports
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import { defaultKeymap, history, indentWithTab } from "@codemirror/commands";
import { historyKeymap } from "@codemirror/commands";
import {
  syntaxHighlighting,
  StreamLanguage,
  HighlightStyle,
} from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { tags } from "@lezer/highlight";

// Language imports (only installed packages)
import { html } from "@codemirror/lang-html";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { xml } from "@codemirror/lang-xml";
import { yaml } from "@codemirror/lang-yaml";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { languages } from "@codemirror/language-data";

/**
 * Custom light theme syntax highlighting with more visible colors.
 * Based on a colorful coding theme for better readability.
 */
const lightHighlightStyle = HighlightStyle.define([
  // Comments
  { tag: tags.comment, color: "#6a737d", fontStyle: "italic" },
  { tag: tags.lineComment, color: "#6a737d", fontStyle: "italic" },
  { tag: tags.blockComment, color: "#6a737d", fontStyle: "italic" },

  // Strings
  { tag: tags.string, color: "#032f62" },
  { tag: tags.special(tags.string), color: "#032f62" },

  // Numbers
  { tag: tags.number, color: "#005cc5" },
  { tag: tags.integer, color: "#005cc5" },
  { tag: tags.float, color: "#005cc5" },

  // Keywords
  { tag: tags.keyword, color: "#d73a49", fontWeight: "bold" },
  { tag: tags.controlKeyword, color: "#d73a49", fontWeight: "bold" },
  { tag: tags.operatorKeyword, color: "#d73a49" },

  // Operators
  { tag: tags.operator, color: "#d73a49" },
  { tag: tags.compareOperator, color: "#d73a49" },
  { tag: tags.arithmeticOperator, color: "#d73a49" },
  { tag: tags.logicOperator, color: "#d73a49" },

  // Definitions
  { tag: tags.definition(tags.variableName), color: "#6f42c1" },
  {
    tag: tags.definition(tags.function(tags.variableName)),
    color: "#6f42c1",
    fontWeight: "bold",
  },

  // Variables and functions
  { tag: tags.variableName, color: "#24292e" },
  { tag: tags.function(tags.variableName), color: "#6f42c1" },

  // Types
  { tag: tags.typeName, color: "#22863a" },
  { tag: tags.className, color: "#22863a", fontWeight: "bold" },
  { tag: tags.namespace, color: "#22863a" },

  // Properties
  { tag: tags.propertyName, color: "#005cc5" },
  { tag: tags.attributeName, color: "#6f42c1" },
  { tag: tags.attributeValue, color: "#032f62" },

  // Markdown specific
  { tag: tags.heading, color: "#005cc5", fontWeight: "bold" },
  {
    tag: tags.heading1,
    color: "#005cc5",
    fontWeight: "bold",
    fontSize: "1.4em",
  },
  {
    tag: tags.heading2,
    color: "#005cc5",
    fontWeight: "bold",
    fontSize: "1.2em",
  },
  {
    tag: tags.heading3,
    color: "#005cc5",
    fontWeight: "bold",
    fontSize: "1.1em",
  },
  { tag: tags.emphasis, fontStyle: "italic", color: "#24292e" },
  { tag: tags.strong, fontWeight: "bold", color: "#24292e" },
  { tag: tags.link, color: "#0366d6", textDecoration: "underline" },
  { tag: tags.url, color: "#0366d6" },
  { tag: tags.quote, color: "#6a737d", fontStyle: "italic" },
  { tag: tags.monospace, color: "#e36209", backgroundColor: "#f6f8fa" },
  { tag: tags.strikethrough, textDecoration: "line-through" },

  // Lists
  { tag: tags.list, color: "#e36209" },

  // HTML/XML tags
  { tag: tags.tagName, color: "#22863a" },
  { tag: tags.angleBracket, color: "#24292e" },

  // Punctuation
  { tag: tags.punctuation, color: "#24292e" },
  { tag: tags.bracket, color: "#24292e" },
  { tag: tags.brace, color: "#24292e" },
  { tag: tags.paren, color: "#24292e" },

  // Special
  { tag: tags.meta, color: "#6a737d" },
  { tag: tags.invalid, color: "#cb2431", backgroundColor: "#ffeef0" },
]);

/**
 * Map of MIME types to their corresponding CodeMirror language extensions.
 * This centralizes language detection logic for all CodeMirror-based components.
 *
 * To add more languages, install the corresponding @codemirror/lang-* package
 * and add the mapping here. Available packages include:
 * - @codemirror/lang-javascript (for JS, TS, JSX, TSX)
 * - @codemirror/lang-css
 * - @codemirror/lang-python
 * - @codemirror/lang-sql
 * - @codemirror/lang-java
 * - @codemirror/lang-php
 * - @codemirror/lang-rust
 * - @codemirror/lang-cpp
 */
const LANGUAGE_MAP = {
  // HTML
  "text/html": () => html(),

  // Markdown (with code block syntax highlighting)
  "text/markdown": () => markdown({ codeLanguages: languages }),
  "text/x-markdown": () => markdown({ codeLanguages: languages }),
  "text/x-web-markdown": () => markdown({ codeLanguages: languages }),

  // JSON
  "application/json": () => json(),
  "text/json": () => json(),

  // XML
  "application/xml": () => xml(),
  "text/xml": () => xml(),

  // YAML
  "application/x-yaml": () => yaml(),
  "text/yaml": () => yaml(),
  "text/x-yaml": () => yaml(),

  // Shell
  "application/x-sh": () => StreamLanguage.define(shell),
  "text/x-sh": () => StreamLanguage.define(shell),
  "application/x-shellscript": () => StreamLanguage.define(shell),
};

/**
 * Returns the CodeMirror language extension for a given MIME type.
 *
 * @param {string} mimeType - The MIME type of the content
 * @returns {Extension[]} The CodeMirror language extension, or an empty array for plain text
 */
export function getLanguageExtension(mimeType) {
  if (!mimeType) {
    return [];
  }

  const factory = LANGUAGE_MAP[mimeType];
  if (factory) {
    return factory();
  }

  return [];
}

/**
 * Returns the base extensions for a CodeMirror editor.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.editable - Whether the editor is editable (default: false)
 * @param {boolean} options.showLineNumbers - Whether to show line numbers (default: true)
 * @param {boolean} options.enableHistory - Whether to enable undo/redo history (default: true for editable)
 * @param {boolean} options.dark - Whether to use dark theme (default: false)
 * @param {boolean} options.lineWrapping - Whether to enable soft line wrapping (default: false)
 * @param {Function} options.onUpdate - Callback function called on document changes
 * @returns {Extension[]} Array of CodeMirror extensions
 */
export function getBaseExtensions(options = {}) {
  const {
    editable = false,
    showLineNumbers = true,
    enableHistory = editable,
    dark = false,
    lineWrapping = false,
    onUpdate = null,
  } = options;

  const extensions = [
    dark
      ? oneDark
      : syntaxHighlighting(lightHighlightStyle, { fallback: true }),
    EditorView.editable.of(editable),
    keymap.of([
      {
        key: "Mod-c",
        run: (view) => {
          const { from, to } = view.state.selection.main;
          if (from === to) {
            return false; // empty selection – let default behaviour handle it
          }
          const text = view.state.sliceDoc(from, to);
          navigator.clipboard.writeText(text).catch(() => {
            // Clipboard API denied – fall back to native copy
          });
          return true;
        },
      },
    ]),
  ];

  if (lineWrapping) {
    extensions.push(EditorView.lineWrapping);
  }

  if (showLineNumbers) {
    extensions.push(lineNumbers());
  }

  if (enableHistory) {
    extensions.push(
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
    );
  } else {
    extensions.push(keymap.of(defaultKeymap));
  }

  if (onUpdate) {
    extensions.push(
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          onUpdate(update.state.doc.toString());
        }
      }),
    );
  }

  return extensions;
}

/**
 * Creates a CodeMirror editor instance.
 *
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.parent - The parent DOM element to attach the editor to
 * @param {string} options.content - The initial content of the editor
 * @param {string} options.mimeType - The MIME type for syntax highlighting
 * @param {boolean} options.editable - Whether the editor is editable (default: false)
 * @param {Function} options.onUpdate - Callback function called on document changes
 * @returns {EditorView} The CodeMirror editor view instance
 */
export function createEditorView(options) {
  const {
    parent,
    content = "",
    mimeType = "text/plain",
    editable = false,
    onUpdate = null,
  } = options;

  if (!parent) {
    throw new Error("Parent element is required to create an editor");
  }

  const langExtension = getLanguageExtension(mimeType);
  const baseExtensions = getBaseExtensions({ editable, onUpdate });

  const extensions = [...baseExtensions];
  if (
    langExtension &&
    (Array.isArray(langExtension) ? langExtension.length > 0 : true)
  ) {
    extensions.push(langExtension);
  }

  const state = EditorState.create({
    doc: content,
    extensions,
  });

  return new EditorView({
    state,
    parent,
  });
}

/**
 * Vue composable for managing CodeMirror editor instances.
 * Provides reactive state management and automatic cleanup.
 *
 * @returns {Object} Composable API
 */
export function useCodeMirror() {
  const editorView = shallowRef(null);

  /**
   * Creates and initializes a CodeMirror editor.
   *
   * @param {Object} options - Same options as createEditorView
   * @returns {EditorView} The created editor view
   */
  const create = (options) => {
    destroy();
    editorView.value = createEditorView(options);
    return editorView.value;
  };

  /**
   * Destroys the current editor instance and cleans up resources.
   */
  const destroy = () => {
    if (editorView.value) {
      editorView.value.destroy();
      editorView.value = null;
    }
  };

  /**
   * Updates the editor content without recreating the editor.
   *
   * @param {string} content - The new content
   */
  const setContent = (content) => {
    if (editorView.value) {
      editorView.value.dispatch({
        changes: {
          from: 0,
          to: editorView.value.state.doc.length,
          insert: content,
        },
      });
    }
  };

  /**
   * Gets the current editor content.
   *
   * @returns {string} The current content, or empty string if no editor
   */
  const getContent = () => {
    if (editorView.value) {
      return editorView.value.state.doc.toString();
    }
    return "";
  };

  return {
    editorView,
    create,
    destroy,
    setContent,
    getContent,
    getLanguageExtension,
    getBaseExtensions,
  };
}

// Re-export CodeMirror modules for components that need direct access
export { EditorState } from "@codemirror/state";
export { EditorView } from "@codemirror/view";
export { oneDark } from "@codemirror/theme-one-dark";
